/**
 * XRP Ledger Regional Alliance + Wave of Innovation coverage.
 * Accents follow the Wave of Innovation brand kit:
 * White · Cyan · Vibrant Blue · Primary Blue · Dark Blue
 */

export type FlagshipOrgId =
  | "wave-of-innovation"
  | "xrpl-commons"
  | "xrp-las-vegas"
  | "xrp-socal"
  | "xrpl-canada"
  | "xrp-asia"
  | "xrpl-japan"
  | "xrpl-korea"
  | "xrpl-malaysia"
  | "xrpl-africa"
  | "xrp-brazil";

export type FlagshipOrg = {
  id: FlagshipOrgId;
  name: string;
  shortName: string;
  mark: string;
  /** Square logo shown in the alliance region pills. */
  logoSrc?: string;
  flagEmoji: string;
  flagSrc?: string;
  regionLabel: string;
  summary: string;
  /** ISO / Natural Earth ADM0_A3 codes highlighted on the map. */
  countryCodes: readonly string[];
  /** US state postal codes (e.g. CA, NV) when coverage is state-level. */
  stateCodes?: readonly string[];
  continent?: string;
  accent: string;
  stroke?: string;
  markOnDark?: boolean;
  planned?: boolean;
  /** Organisation website. */
  href?: string;
  /** Organisation account on X. */
  xHref?: string;
  /** Full-bleed banner at the top of the region pop-out. */
  bannerSrc?: string;
  /** Optional blog / feature article linked from the pop-out. */
  articleHref?: string;
  articleLabel?: string;
  /** Line above social links; defaults to “Become part of {shortName}.” */
  involveCta?: string;
  external?: boolean;
  defaultEventId: string;
};

export const FLAGSHIP_XRP_ORGS: readonly FlagshipOrg[] = [
  {
    id: "wave-of-innovation",
    name: "Wave of Innovation",
    shortName: "Wave of Innovation",
    mark: "WoI",
    logoSrc: "/xrpl_regions/x_logo_v8.png",
    bannerSrc: "/images/leadership_image.png",
    flagEmoji: "🇦🇺",
    regionLabel: "Australia & UK",
    summary:
      "An independent non-profit events and media organisation based in Australia and operating in London. We bridge Commonwealth financial hubs through the XRP Australia and XRP London flagships, where institutions, builders, and the community meet, learn, and leave with real relationships.",
    countryCodes: ["AUS", "GBR"],
    accent: "#21e46b",
    href: "https://www.waveofinnovation.com/",
    xHref: "https://x.com/wave_of_innov",
    articleHref: "/about",
    articleLabel: "About us",
    involveCta: "Become part of Wave of Innovation.",
    external: true,
    defaultEventId: "xrp-australia-2027",
  },
  {
    id: "xrpl-commons",
    name: "XRPL Commons",
    shortName: "XRPL Commons",
    mark: "XC",
    logoSrc: "/xrpl_regions/xrpl_commons.jpg",
    bannerSrc: "/blog/commons_graphtag.png",
    flagEmoji: "🇫🇷",
    regionLabel: "France & Europe",
    summary:
      "The XRPL hub for Europe, based in Paris. This non-profit educates developers and the wider community, runs research partnerships, and hosts the Aquarium residency so builders can ship real projects on the XRPL.",
    countryCodes: ["FRA"],
    accent: "#6b6bf2",
    markOnDark: true,
    href: "https://www.xrpl-commons.org/",
    xHref: "https://x.com/XRPLCommons",
    articleHref: "/blog/xrpl-commons-xrp-australia-2026",
    articleLabel: "Read our article",
    involveCta: "Become part of XRPL Commons.",
    external: true,
    defaultEventId: "xrpl-commons-paris",
  },
  {
    id: "xrp-las-vegas",
    name: "XRP Las Vegas",
    shortName: "XRP Las Vegas",
    mark: "LV",
    logoSrc: "/xrpl_regions/xrp_lv.png",
    bannerSrc: "/xrpl_regions/xrp_lv.png",
    flagEmoji: "🇺🇸",
    regionLabel: "Nevada",
    summary:
      "The community-run North American XRP flagship in Las Vegas. They bring holders, creators, and builders together for a major annual gathering in the United States.",
    countryCodes: [],
    stateCodes: ["NV"],
    accent: "#0d4575",
    markOnDark: true,
    href: "https://www.xrplasvegas.com/",
    xHref: "https://x.com/DigPerspectives",
    involveCta: "Become part of XRP Las Vegas.",
    external: true,
    defaultEventId: "xrp-vegas-2027",
  },
  {
    id: "xrp-socal",
    name: "XRP SoCal",
    shortName: "XRP SoCal",
    mark: "SC",
    logoSrc: "/xrpl_regions/xrp_socal.jpg",
    bannerSrc: "/xrpl_regions/xrp_socal.jpg",
    flagEmoji: "🇺🇸",
    regionLabel: "California",
    summary:
      "Southern California’s doorway to the XRP Ledger. They foster blockchain adoption through art, tech, and entertainment, bringing builders and community together in Orange County and beyond.",
    countryCodes: [],
    stateCodes: ["CA"],
    accent: "#27c4e8",
    markOnDark: true,
    xHref: "https://x.com/XRPSoCal",
    involveCta: "Become part of XRP SoCal.",
    external: true,
    defaultEventId: "xrp-socal-hub",
  },
  {
    id: "xrpl-canada",
    name: "XRPL Canada",
    shortName: "XRPL Canada",
    mark: "CA",
    logoSrc: "/xrpl_regions/xrpl_canada.jpg",
    flagEmoji: "🇨🇦",
    regionLabel: "Canada",
    summary:
      "Canada’s XRPL community hub. They connect holders, builders, and operators across the country through local rooms, meetups, and shared updates.",
    countryCodes: ["CAN"],
    accent: "#ffffff",
    stroke: "#0d4575",
    planned: true,
    href: "https://xrplcanada.org/",
    xHref: "https://x.com/XRPLCanada",
    involveCta: "Become part of XRPL Canada.",
    external: true,
    defaultEventId: "xrpl-canada-hub",
  },
  {
    id: "xrp-asia",
    name: "XRP Asia",
    shortName: "XRP Asia",
    mark: "XA",
    logoSrc: "/xrpl_regions/xrp_asia.jpg",
    bannerSrc: "/xrpl_regions/xrp_asia_banner.png",
    flagEmoji: "🇸🇬",
    regionLabel: "Singapore & APAC",
    summary:
      "The XRPL hub for Asia-Pacific (APAC), based in Singapore. They support builders and local communities with education, networking, and regional gathering points.",
    countryCodes: ["SGP"],
    /** Primary blue — keeps Singapore distinct from Wave of Innovation cyan. */
    accent: "#0d4575",
    markOnDark: true,
    href: "https://xrpasia.org/",
    xHref: "https://x.com/XRPAsiaOfficial",
    involveCta: "Become part of XRP Asia.",
    external: true,
    defaultEventId: "xrp-asia-singapore",
  },
  {
    id: "xrpl-japan",
    name: "XRPL Japan",
    shortName: "XRPL Japan",
    mark: "JP",
    logoSrc: "/xrpl_regions/xrp_japan.jpg",
    bannerSrc: "/xrpl_regions/xrp_japan_banner.png",
    flagEmoji: "🇯🇵",
    regionLabel: "Japan",
    summary:
      "Japan’s XRPL association. They run XRP Japan / Tokyo gatherings and help Japanese holders, developers, and partners engage with the ledger in their own language and market.",
    countryCodes: ["JPN"],
    accent: "#ffffff",
    href: "https://xrpl.jp/",
    xHref: "https://x.com/XRPLJapan",
    involveCta: "Become part of XRPL Japan.",
    external: true,
    defaultEventId: "xrp-japan-2027",
  },
  {
    id: "xrpl-korea",
    name: "XRPL Korea",
    shortName: "XRPL Korea",
    mark: "KR",
    logoSrc: "/xrpl_regions/xrpl_korea.jpg",
    bannerSrc: "/xrpl_regions/xrp_korea_banner.png",
    flagEmoji: "🇰🇷",
    regionLabel: "South Korea",
    summary:
      "Korea’s XRPL organisation. They host XRP Seoul and related programmes so Korean holders, builders, and institutions have a dedicated place to learn and connect.",
    countryCodes: ["KOR"],
    accent: "#6b6bf2",
    markOnDark: true,
    href: "https://xrplkorea.org/",
    xHref: "https://x.com/xrplkorea",
    involveCta: "Become part of XRPL Korea.",
    external: true,
    defaultEventId: "xrp-korea-2027",
  },
  {
    id: "xrpl-malaysia",
    name: "XRPL Malaysia",
    shortName: "XRPL Malaysia",
    mark: "MY",
    logoSrc: "/xrpl_regions/xrp_malaysia.jpg",
    bannerSrc: "/xrpl_regions/xrp_malaysia_banner.png",
    flagEmoji: "🇲🇾",
    regionLabel: "Malaysia",
    summary:
      "Malaysia’s XRPL community. They build local rooms and meetups so holders, builders, and developers can learn and collaborate without needing to leave the region.",
    countryCodes: ["MYS"],
    accent: "#ffffff",
    stroke: "#6b6bf2",
    planned: true,
    xHref: "https://x.com/xrplmalaysia",
    involveCta: "Become part of XRPL Malaysia.",
    external: true,
    defaultEventId: "xrp-malaysia-2028",
  },
  {
    id: "xrpl-africa",
    name: "XRPL Africa",
    shortName: "XRPL Africa",
    mark: "AF",
    logoSrc: "/xrpl_regions/xrpl_africa.jpg",
    bannerSrc: "/xrpl_regions/xrp_africa_banner.png",
    flagEmoji: "🌍",
    flagSrc: "/flags/african_union.svg",
    regionLabel: "Africa - Nigeria",
    summary:
      "The only XRP hub in Africa, based in Nigeria. Their mission is to grow the XRPL across the continent and help more African countries join the XRP Ledger community.",
    countryCodes: ["NGA"],
    accent: "#05192f",
    stroke: "#27c4e8",
    markOnDark: true,
    planned: true,
    href: "https://xrplafrica.org/",
    xHref: "https://x.com/XRPL_AF",
    involveCta: "Become part of XRPL Africa.",
    external: true,
    defaultEventId: "xrp-africa-2028",
  },
  {
    id: "xrp-brazil",
    name: "XRP Brazil",
    shortName: "XRP Brazil",
    mark: "BR",
    logoSrc: "/xrpl_regions/xrp_brazil.jpg",
    bannerSrc: "/xrpl_regions/xrp_brazil_banner.png",
    flagEmoji: "🇧🇷",
    regionLabel: "Brazil",
    summary:
      "Brazil’s XRPL education and community hub. They create Portuguese-language content and gatherings so Brazilian holders and builders can take part in the ecosystem in their own language.",
    countryCodes: ["BRA"],
    accent: "#21e46b",
    markOnDark: true,
    planned: true,
    href: "https://xrpbrasil.com.br/",
    xHref: "https://x.com/Brasil_Xrp",
    involveCta: "Become part of XRP Brazil.",
    external: true,
    defaultEventId: "xrp-brazil-hub",
  },
];

export function getFlagshipOrg(id: FlagshipOrgId): FlagshipOrg {
  return FLAGSHIP_XRP_ORGS.find((org) => org.id === id) ?? FLAGSHIP_XRP_ORGS[0];
}

/** Compact alliance pills above the map: logo + flag + region name. */
export type FlagshipRegion = {
  id: string;
  orgId: FlagshipOrgId;
  label: string;
  flagEmoji: string;
  flagSrc?: string;
  defaultEventId: string;
  /** Used when one org covers more than one region chip. */
  placeId?: string;
};

/** Chip order: Canada → Commons → Asia → Korea → Japan, then Vegas → SoCal → Brazil → Africa → Malaysia → WoI. */
export const FLAGSHIP_REGIONS: readonly FlagshipRegion[] = [
  {
    id: "canada",
    orgId: "xrpl-canada",
    label: "Canada",
    flagEmoji: "🇨🇦",
    defaultEventId: "xrpl-canada-hub",
  },
  {
    id: "france",
    orgId: "xrpl-commons",
    label: "France & Europe",
    flagEmoji: "🇫🇷",
    defaultEventId: "xrpl-commons-paris",
  },
  {
    id: "singapore",
    orgId: "xrp-asia",
    label: "Singapore & APAC",
    flagEmoji: "🇸🇬",
    defaultEventId: "xrp-asia-singapore",
  },
  {
    id: "south-korea",
    orgId: "xrpl-korea",
    label: "South Korea",
    flagEmoji: "🇰🇷",
    defaultEventId: "xrp-korea-2027",
  },
  {
    id: "japan",
    orgId: "xrpl-japan",
    label: "Japan",
    flagEmoji: "🇯🇵",
    defaultEventId: "xrp-japan-2027",
  },
  {
    id: "united-states",
    orgId: "xrp-las-vegas",
    label: "Nevada",
    flagEmoji: "🇺🇸",
    defaultEventId: "xrp-vegas-2027",
  },
  {
    id: "southern-california",
    orgId: "xrp-socal",
    label: "California",
    flagEmoji: "🇺🇸",
    defaultEventId: "xrp-socal-hub",
  },
  {
    id: "brazil",
    orgId: "xrp-brazil",
    label: "Brazil",
    flagEmoji: "🇧🇷",
    defaultEventId: "xrp-brazil-hub",
  },
  {
    id: "nigeria",
    orgId: "xrpl-africa",
    label: "Africa - Nigeria",
    flagEmoji: "🌍",
    flagSrc: "/flags/african_union.svg",
    defaultEventId: "xrp-africa-2028",
  },
  {
    id: "malaysia",
    orgId: "xrpl-malaysia",
    label: "Malaysia",
    flagEmoji: "🇲🇾",
    defaultEventId: "xrp-malaysia-2028",
  },
  {
    id: "wave-of-innovation",
    orgId: "wave-of-innovation",
    label: "Australia & UK",
    flagEmoji: "🇦🇺",
    defaultEventId: "xrp-australia-2027",
  },
];

export function orgAccentFill(hex: string, alpha: number): string {
  const raw = hex.replace("#", "");
  if (raw.length !== 6) return hex;
  const r = Number.parseInt(raw.slice(0, 2), 16);
  const g = Number.parseInt(raw.slice(2, 4), 16);
  const b = Number.parseInt(raw.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
